import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Fires page-view events on every React Router route change to keep
 * client-side navigation counted by our analytics stack.
 *
 * Two integrations, one hook — cheaper than mounting two identical
 * useLocation subscribers:
 *   1. **Meta Pixel**  — `fbq('track', 'PageView')` (the inline snippet
 *      in `public/index.html` only reports the first hit).
 *   2. **Google Tag Manager** — pushes `{ event: 'pageview', page_path,
 *      page_location }` onto `window.dataLayer` so GTM containers with
 *      GA4 / Ads / any downstream tag can trigger on SPA transitions.
 *
 * Mounted once at the top of the App tree (right under <BrowserRouter>).
 * No-ops if either global is missing (dev without the snippet, or ad
 * blocker scrubbed the script). Never throws.
 */
export default function MetaPixelRouteTracker() {
  const location = useLocation();

  useEffect(() => {
    // Meta Pixel
    try {
      if (typeof window !== 'undefined' && typeof window.fbq === 'function') {
        window.fbq('track', 'PageView');
      }
    } catch {
      /* fbq missing / blocked — silently ignore */
    }
    // Google Tag Manager
    try {
      if (typeof window !== 'undefined' && Array.isArray(window.dataLayer)) {
        window.dataLayer.push({
          event: 'pageview',
          page_path: location.pathname + location.search,
          page_location: typeof window !== 'undefined' ? window.location.href : undefined,
          page_title: typeof document !== 'undefined' ? document.title : undefined,
        });
      }
    } catch {
      /* dataLayer missing / blocked — silently ignore */
    }
  }, [location.pathname, location.search]);

  return null;
}
